import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChatBubbleLeftIcon, DocumentTextIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { VoiceButton } from '../../ChatInDoc/VoiceButton';
import { MessageAudioControls } from '../../ChatInDoc/MessageAudioControls';
import { voiceService } from '../../../services/voiceService';
import { Conversation, ConversationConductor, getAssumeHintReason } from '../../../types/conversation';
import { ConversationLabel } from '../../../types/label';
import { SupportMessage } from '../../../types/supportMessage';
import { SupportGroup } from '../../../types/supportGroup';
import { CannedResponse } from '../../../types/cannedResponse';
import { ChannelType, ConversationStatus, SupportMessageType, SupportSenderType, defaultEnterSendsMessage, isConversationClosedForMessaging } from '../../../types/atendimentoEnums';
import { InboxFormBinding } from '../../../types/inbox';
import { cannedResponseService } from '../../../services/cannedResponseService';
import { inboxService } from '../../../services/inboxService';
import { useToast } from '../../UI/ToastProvider';
import { WorkspaceThemeClasses } from './workspaceTheme';
import { WorkspaceChatActions } from './WorkspaceChatActions';
import { WorkspaceAssumeHintBanner } from './WorkspaceAssumeHintBanner';
import { WorkspaceHistoryPanel } from './WorkspaceHistoryPanel';
import { EmailMessageBubble } from './EmailMessageBubble';
import { SupportBotMessageContent } from './SupportBotMessageContent';
import { WorkspaceCsatCard } from './WorkspaceCsatCard';
import { WorkspaceFormCard } from './WorkspaceFormCard';
import { userSignatureService } from '../../../services/userSignatureService';
import { helpdeskRoutes } from '../../../routes/helpdeskRoutes';
import { ClassificationBadges } from './ClassificationBadges';
import { resolveClassificationBadges } from '../../../utils/classificationBadgeDisplay';
import { formatUnreadDividerLabel, resolveUnreadDivider, resolveWorkspaceEmailSubject } from '../../../utils/conversationQueuePreview';
import { parseSupportMessageCsat } from '../../../utils/supportMessageCsat';
import { parseSupportMessageMedia } from '../../../utils/supportMessageMedia';
import { SupportMessageMediaGallery } from './SupportMessageMediaGallery';
import { parseSupportMessageForm } from '../../../utils/supportMessageForm';
import { FormPublicLinkText, formLinkTitlesFromBindings } from '../../../utils/formPublicLink';
import {
  canRetrySupportMessage,
  isFailedSupportMessage,
  isPendingSupportMessage,
  isPublicOutgoingMessage,
  parseSupportMessageDelivery,
} from '../../../utils/supportMessageDelivery';

interface WorkspaceChatProps {
  theme: WorkspaceThemeClasses;
  conversation: Conversation | null;
  messages: SupportMessage[];
  groups: SupportGroup[];
  selectedGroupId: string;
  loading: boolean;
  draft: string;
  sending: boolean;
  retryingMessageId?: string | null;
  actionLoading: boolean;
  conductor: ConversationConductor | null;
  currentUserId?: string | null;
  canAssume: boolean;
  canHandoffToHuman: boolean;
  canHandoffToBot: boolean;
  canCapture: boolean;
  canReopen: boolean;
  conversationLabels?: ConversationLabel[];
  spacious?: boolean;
  onDraftChange: (value: string) => void;
  onSend: (content?: string, contentAttributes?: string) => void;
  onRetryMessage?: (messageId: string) => void;
  onSelectedGroupChange: (groupId: string) => void;
  onTransferGroup: () => void;
  onAssume: () => void | Promise<void>;
  onHandoffToHuman: () => void;
  onHandoffToBot: () => void;
  onUpdateStatus: (status: ConversationStatus) => void;
  onResolve: () => void;
  onReopen: () => void;
  onDelete: () => void;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onTranscriptVisible?: (conversationId: string) => void;
  onUserReadIntent?: () => void;
  onSelectConversation: (id: string) => void;
  contactPanelOpen: boolean;
  onToggleContactPanel: () => void;
  onBackToQueue?: () => void;
}

const bubbleClass = (
  theme: WorkspaceThemeClasses,
  senderType: SupportSenderType,
  messageType: SupportMessageType
): { wrap: string; bubble: string } => {
  if (senderType === SupportSenderType.Bot)
  {
    return { wrap: 'self-end', bubble: theme.bubbleBot };
  }

  if (senderType === SupportSenderType.AppUser || messageType === SupportMessageType.Outgoing)
  {
    return { wrap: 'self-end', bubble: theme.bubbleOut };
  }

  return { wrap: 'self-start', bubble: theme.bubbleIn };
};

const senderLabel = (
  senderType: SupportSenderType,
  messageType: SupportMessageType,
  contactName: string,
  senderName?: string | null,
): string => {
  if (senderType === SupportSenderType.Bot)
  {
    return 'Bot';
  }

  if (senderType === SupportSenderType.AppUser || messageType === SupportMessageType.Outgoing)
  {
    return senderName || 'Você';
  }

  if (senderType === SupportSenderType.System)
  {
    return 'Sistema';
  }

  return contactName.split(/\s+/)[0] ?? 'Contato';
};

const canSpeakWorkspaceMessage = (
  channelType: ChannelType | undefined,
  message: SupportMessage,
): boolean => {
  if (channelType === ChannelType.Email)
  {
    return false;
  }

  if (message.messageType === SupportMessageType.Activity)
  {
    return false;
  }

  return Boolean(message.content?.trim());
};

const senderInitial = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter((part) => part.length > 0);
  if (parts.length === 0)
  {
    return '?';
  }

  return parts[0][0].toUpperCase();
};

const senderAvatarColor = (name: string): string => {
  const palette = ['#047857', '#1d4ed8', '#7c3aed', '#b45309', '#0f766e', '#be185d'];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1)
  {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return palette[Math.abs(hash) % palette.length];
};

const getSlashQuery = (value: string): { query: string; startIndex: number } | null => {
  const slashIndex = value.lastIndexOf('/');
  if (slashIndex === -1)
  {
    return null;
  }

  if (slashIndex > 0)
  {
    const prev = value[slashIndex - 1];
    if (prev !== ' ' && prev !== '\n')
    {
      return null;
    }
  }

  const afterSlash = value.slice(slashIndex + 1);
  if (afterSlash.includes(' ') || afterSlash.includes('\n'))
  {
    return null;
  }

  return { query: afterSlash.toLowerCase(), startIndex: slashIndex };
};

const formSlashCaption = 'Adiciona o link na mensagem';

type SlashEntry =
  | { kind: 'canned'; key: string; canned: CannedResponse }
  | { kind: 'form'; key: string; form: InboxFormBinding };

export const WorkspaceChat: React.FC<WorkspaceChatProps> = ({
  theme,
  conversation,
  messages,
  groups,
  selectedGroupId,
  loading,
  draft,
  sending,
  retryingMessageId = null,
  actionLoading,
  conductor,
  currentUserId,
  canAssume,
  canHandoffToHuman,
  canHandoffToBot,
  canCapture,
  canReopen,
  conversationLabels = [],
  spacious = false,
  onDraftChange,
  onSend,
  onRetryMessage,
  onSelectedGroupChange,
  onTransferGroup,
  onAssume,
  onHandoffToHuman,
  onHandoffToBot,
  onUpdateStatus,
  onResolve,
  onReopen,
  onDelete,
  onMarkRead,
  onMarkUnread,
  onTranscriptVisible,
  onUserReadIntent,
  onSelectConversation,
  contactPanelOpen,
  onToggleContactPanel,
  onBackToQueue,
}) => {
  const { addToast } = useToast();
  const [unreadAnchor, setUnreadAnchor] = useState<{ id: string; lastSeenAt: string | null } | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const firstUnreadRef = useRef<HTMLDivElement | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [cannedResponses, setCannedResponses] = useState<CannedResponse[]>([]);
  const [inboxForms, setInboxForms] = useState<InboxFormBinding[]>([]);
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashHighlightIndex, setSlashHighlightIndex] = useState(0);
  const [hintDismissedConversationId, setHintDismissedConversationId] = useState<string | null>(null);
  const [messageSignature, setMessageSignature] = useState('');
  const [hasSignatureImage, setHasSignatureImage] = useState(false);
  const [includeSignature, setIncludeSignature] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const hasPendingOutgoing = messages.some((message) => isPendingSupportMessage(message));

  useEffect(() => {
    if (!hasPendingOutgoing)
    {
      return;
    }

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasPendingOutgoing]);
  const isEmailConversation = conversation?.channelType === ChannelType.Email;
  const enterSendsMessage = conversation
    ? (conversation.enterSendsMessage ?? defaultEnterSendsMessage(conversation.channelType))
    : true;
  const isClosedForMessaging = conversation
    ? isConversationClosedForMessaging(conversation.status)
    : false;

  const classificationBadges = useMemo(
    () => resolveClassificationBadges(conversation),
    [conversation?.classificationBadges, conversation?.classificationResult],
  );

  const emailSubject = useMemo(
    () => resolveWorkspaceEmailSubject(conversation, messages),
    [conversation, messages],
  );

  const unreadDivider = useMemo(() => {
    if (!conversation)
    {
      return { firstIndex: -1, count: 0 };
    }

    const liveUnread = conversation.unreadCount ?? 0;
    const hasOpenAnchor = unreadAnchor?.id === conversation.id;
    const lastSeen = liveUnread > 0
      ? conversation.agentLastSeenAt
      : (hasOpenAnchor ? unreadAnchor.lastSeenAt : conversation.agentLastSeenAt);

    if (liveUnread <= 0 && !hasOpenAnchor && !lastSeen)
    {
      return { firstIndex: -1, count: 0 };
    }

    return resolveUnreadDivider(messages, lastSeen);
  }, [conversation, messages, unreadAnchor]);

  useLayoutEffect(() => {
    if (loading)
    {
      return;
    }

    if (!conversation?.id)
    {
      setUnreadAnchor(null);
      return;
    }

    const conversationId = conversation.id;
    const lastSeenAt = conversation.agentLastSeenAt ?? null;

    setUnreadAnchor({
      id: conversationId,
      lastSeenAt,
    });
    onTranscriptVisible?.(conversationId);
  }, [conversation?.id, loading, onTranscriptVisible]);

  useEffect(() => {
    if (!isEmailConversation || !currentUserId)
    {
      setMessageSignature('');
      setHasSignatureImage(false);
      setIncludeSignature(false);
      return;
    }

    const savedPreference = window.localStorage.getItem(
      `email_signature_enabled:${currentUserId}`,
    );
    setIncludeSignature(savedPreference === 'true');

    const loadSignature = async () => {
      try
      {
        const signature = await userSignatureService.get();
        setMessageSignature(signature.messageSignature);
        setHasSignatureImage(Boolean(signature.imageUrl));
      }
      catch
      {
        addToast('Erro ao carregar assinatura de e-mail', 'error');
      }
    };

    void loadSignature();
  }, [addToast, currentUserId, isEmailConversation]);

  useEffect(() => {
    setHintDismissedConversationId(null);
  }, [conversation?.id, conversation?.status, conversation?.assigneeId]);

  const assumeHintReason = useMemo(() => {
    if (!conversation)
    {
      return null;
    }

    return getAssumeHintReason({
      conversation,
      currentUserId,
      draft,
      dismissed: hintDismissedConversationId === conversation.id,
      inboxHasActiveBot: conductor === 'bot',
    });
  }, [conversation, currentUserId, draft, hintDismissedConversationId, conductor]);

  const handleAssumeFromHint = async () => {
    await onAssume();
    requestAnimationFrame(() => {
      messageInputRef.current?.focus();
    });
  };

  const handleDismissAssumeHint = () => {
    if (!conversation)
    {
      return;
    }

    setHintDismissedConversationId(conversation.id);
    requestAnimationFrame(() => {
      messageInputRef.current?.focus();
    });
  };

  useEffect(() => {
    if (loading)
    {
      return;
    }

    if (unreadDivider.count > 0 && firstUnreadRef.current)
    {
      firstUnreadRef.current.scrollIntoView({ block: 'start' });
      return;
    }

    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, conversation?.id, loading, unreadDivider.count]);

  useEffect(() => {
    if (!conversation?.id || sending || isClosedForMessaging)
    {
      return;
    }

    messageInputRef.current?.focus();
  }, [sending, conversation?.id, isClosedForMessaging]);

  useEffect(() => {
    return () => {
      voiceService.stopRecording();
      voiceService.stopSpeaking();
    };
  }, [conversation?.id]);

  useEffect(() => {
    const loadCannedResponses = async () => {
      try
      {
        const data = await cannedResponseService.getAll();
        setCannedResponses(data);
      }
      catch
      {
        addToast('Erro ao carregar respostas prontas', 'error');
      }
    };

    void loadCannedResponses();
  }, [addToast]);

  useEffect(() => {
    if (!conversation?.inboxId)
    {
      setInboxForms([]);
      return;
    }

    let cancelled = false;
    const loadInboxForms = async () => {
      try
      {
        const forms = await inboxService.getForms(conversation.inboxId);
        if (!cancelled)
        {
          setInboxForms(forms);
        }
      }
      catch
      {
        if (!cancelled)
        {
          setInboxForms([]);
        }
      }
    };

    void loadInboxForms();
    return () => {
      cancelled = true;
    };
  }, [conversation?.inboxId]);

  const formLinkTitles = useMemo(
    () => formLinkTitlesFromBindings(inboxForms),
    [inboxForms],
  );

  const filteredCannedSuggestions = useMemo(() => {
    const slash = getSlashQuery(draft);
    if (!slash)
    {
      return [];
    }

    return cannedResponses.filter((item) =>
      item.shortCode.toLowerCase().startsWith(slash.query)
      || item.title.toLowerCase().includes(slash.query),
    );
  }, [cannedResponses, draft]);

  const filteredFormSuggestions = useMemo(() => {
    const slash = getSlashQuery(draft);
    if (!slash || !conversation || isClosedForMessaging)
    {
      return [];
    }

    return inboxForms.filter((item) => {
      if (item.formAccessType === 'Private')
      {
        return false;
      }

      if (!slash.query)
      {
        return true;
      }

      return (item.formTitle || '').toLowerCase().includes(slash.query);
    });
  }, [conversation, draft, inboxForms, isClosedForMessaging]);

  const slashEntries = useMemo<SlashEntry[]>(() => [
    ...filteredCannedSuggestions.map((item) => ({
      kind: 'canned' as const,
      key: `canned-${item.id}`,
      canned: item,
    })),
    ...filteredFormSuggestions.map((item) => ({
      kind: 'form' as const,
      key: `form-${item.formId}`,
      form: item,
    })),
  ], [filteredCannedSuggestions, filteredFormSuggestions]);

  const highlightedSlashIndex = slashEntries.length === 0
    ? 0
    : Math.min(slashHighlightIndex, slashEntries.length - 1);

  const closeSlashMenu = () => {
    setShowSlashMenu(false);
    setSlashHighlightIndex(0);
  };

  const applyCannedResponse = (item: CannedResponse) => {
    const slash = getSlashQuery(draft);
    if (!slash)
    {
      return;
    }

    const before = draft.slice(0, slash.startIndex);
    onDraftChange(before + item.content);
    closeSlashMenu();
    messageInputRef.current?.focus();
  };

  const applyInboxForm = (form: InboxFormBinding) => {
    const slash = getSlashQuery(draft);
    if (!slash)
    {
      return;
    }

    if (!form.publicUrl)
    {
      addToast(
        'Não foi possível montar o link público do formulário. O cliente não tem domínio público configurado.',
        'error',
      );
      return;
    }

    const before = draft.slice(0, slash.startIndex);
    onDraftChange(before + form.publicUrl);
    closeSlashMenu();
    messageInputRef.current?.focus();
  };

  const applySlashEntry = (entry: SlashEntry) => {
    if (entry.kind === 'canned')
    {
      applyCannedResponse(entry.canned);
      return;
    }

    applyInboxForm(entry.form);
  };

  const handleUserReadIntent = () => {
    onUserReadIntent?.();
    if (!conversation?.id)
    {
      return;
    }

    setUnreadAnchor((current) => {
      if (current?.id === conversation.id && unreadDivider.count <= 0)
      {
        return current;
      }

      return {
        id: conversation.id,
        lastSeenAt: new Date().toISOString(),
      };
    });
  };

  const handleDraftChange = (value: string) => {
    handleUserReadIntent();
    onDraftChange(value);
    const slash = getSlashQuery(value);
    if (slash)
    {
      setShowSlashMenu(true);
      setSlashHighlightIndex(0);
    }
    else
    {
      setShowSlashMenu(false);
    }
  };

  const handleMessageKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSlashMenu && slashEntries.length > 0)
    {
      if (event.key === 'ArrowDown')
      {
        event.preventDefault();
        setSlashHighlightIndex((prev) => {
          const current = Math.min(prev, slashEntries.length - 1);
          return current < slashEntries.length - 1 ? current + 1 : 0;
        });
        return;
      }

      if (event.key === 'ArrowUp')
      {
        event.preventDefault();
        setSlashHighlightIndex((prev) => {
          const current = Math.min(prev, slashEntries.length - 1);
          return current > 0 ? current - 1 : slashEntries.length - 1;
        });
        return;
      }

      if (event.key === 'Escape')
      {
        event.preventDefault();
        setShowSlashMenu(false);
        return;
      }

      if (event.key === 'Enter')
      {
        event.preventDefault();
        const selectedEntry = slashEntries[highlightedSlashIndex];
        if (selectedEntry)
        {
          applySlashEntry(selectedEntry);
        }
        return;
      }
    }

    if (event.key !== 'Enter' || event.nativeEvent.isComposing)
    {
      return;
    }

    const shouldSend = enterSendsMessage
      ? !event.shiftKey
      : (event.ctrlKey || event.metaKey);

    if (shouldSend)
    {
      event.preventDefault();
      sendDraft();
    }
  };

  const handleVoiceResult = (text: string) => {
    const transcript = text.trim();
    if (!transcript)
    {
      return;
    }

    const current = draft.trim();
    handleDraftChange(current ? `${current} ${transcript}` : transcript);
    requestAnimationFrame(() => {
      messageInputRef.current?.focus();
    });
  };

  const handleVoiceError = (error: string) => {
    addToast(error, 'error');
  };

  const sendDraft = () => {
    if (sending || isClosedForMessaging)
    {
      return;
    }

    if (showSlashMenu && slashEntries.length > 0)
    {
      return;
    }

    const content = draft.trim();
    const hasSignature = messageSignature.trim().length > 0 || hasSignatureImage;
    const shouldIncludeSignature = isEmailConversation && includeSignature && hasSignature;
    const contentAttributes = shouldIncludeSignature
      ? JSON.stringify({ emailSignature: { include: true } })
      : undefined;

    voiceService.stopRecording();
    voiceService.stopSpeaking();
    onSend(content, contentAttributes);
    setShowSlashMenu(false);
    requestAnimationFrame(() => {
      messageInputRef.current?.focus();
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    sendDraft();
  };

  const toggleSignature = () => {
    if (!currentUserId)
    {
      return;
    }

    const nextValue = !includeSignature;
    setIncludeSignature(nextValue);
    window.localStorage.setItem(
      `email_signature_enabled:${currentUserId}`,
      String(nextValue),
    );
  };

  if (!conversation && !loading)
  {
    return (
      <div className={`flex-1 flex items-center justify-center ${theme.surfaceAlt}`}>
        <p className={`text-sm ${theme.muted}`}>Selecione uma conversa na fila.</p>
      </div>
    );
  }

  return (
    <section className={`flex-1 min-w-0 flex flex-col overflow-hidden ${theme.surfaceAlt} relative`}>
      {conversation ? (
        <div className="relative">
          <WorkspaceChatActions
            theme={theme}
            conversation={conversation}
            groups={groups}
            selectedGroupId={selectedGroupId}
            actionLoading={actionLoading}
            conductor={conductor}
            canAssume={canAssume}
            canHandoffToHuman={canHandoffToHuman}
            canHandoffToBot={canHandoffToBot}
            canCapture={canCapture}
            canReopen={canReopen}
            classificationBadges={classificationBadges}
            emailSubject={emailSubject}
            onSelectedGroupChange={onSelectedGroupChange}
            onTransferGroup={onTransferGroup}
            onAssume={onAssume}
            onHandoffToHuman={onHandoffToHuman}
            onHandoffToBot={onHandoffToBot}
            onUpdateStatus={onUpdateStatus}
            onResolve={onResolve}
            onReopen={onReopen}
            onDelete={onDelete}
            onMarkRead={onMarkRead}
            onMarkUnread={onMarkUnread}
            onShowHistory={() => setShowHistory(!showHistory)}
            contactPanelOpen={contactPanelOpen}
            onToggleContactPanel={() => {
              if (!contactPanelOpen)
              {
                setShowHistory(false);
              }

              onToggleContactPanel();
            }}
            onBackToQueue={onBackToQueue}
          />
          <WorkspaceHistoryPanel
            theme={theme}
            conversationId={conversation.id}
            isVisible={showHistory}
            onClose={() => setShowHistory(false)}
            onSelectConversation={onSelectConversation}
          />
        </div>
      ) : (
        <div className={`flex-shrink-0 px-4 py-3 border-b text-sm ${theme.surface} ${theme.border} ${theme.muted}`}>
          Carregando conversa…
        </div>
      )}

      <div
        className={`flex-1 overflow-y-auto flex flex-col gap-3 ${spacious ? 'px-10 py-6' : 'px-5 py-4'}`}
        onPointerDown={handleUserReadIntent}
      >
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          </div>
        ) : (
          <>
            {messages.length === 0 && (
              <p className={`text-center text-sm py-8 ${theme.muted}`}>
                Nenhuma mensagem nesta conversa.
              </p>
            )}
            {messages.map((message, index) => {
              const tone = bubbleClass(theme, message.senderType, message.messageType);
              const time = new Date(message.createdAt).toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
              });
              const isAgentReply = message.senderType === SupportSenderType.AppUser;
              const agentName = message.senderName?.trim() || '';
              const showAgentAvatar = isAgentReply && agentName.length > 0;
              const isFirstUnread = index === unreadDivider.firstIndex && unreadDivider.count > 0;

              return (
                <React.Fragment key={message.id}>
                  {isFirstUnread && (
                    <div
                      ref={firstUnreadRef}
                      className="flex items-center gap-2 w-full self-stretch"
                    >
                      <span className="flex-1 h-px bg-blue-200" />
                      <span className="text-[11px] font-semibold text-blue-700 whitespace-nowrap">
                        {formatUnreadDividerLabel(unreadDivider.count)}
                      </span>
                      <span className="flex-1 h-px bg-blue-200" />
                    </div>
                  )}
                  <div
                    className={`${conversation?.channelType === ChannelType.Email ? 'w-[90%] max-w-[900px]' : 'max-w-[75%]'} flex flex-col ${tone.wrap === 'self-end' ? 'self-end' : 'self-start'}`}
                  >
                  <div className={`flex items-end gap-1.5 ${tone.wrap === 'self-end' ? 'flex-row' : 'flex-row'}`}>
                  <div className={`min-w-0 flex flex-col gap-1 ${conversation?.channelType === ChannelType.Email ? 'flex-1' : ''}`}>
                    {parseSupportMessageCsat(message.contentAttributes).isCsat ? (
                      <WorkspaceCsatCard
                        content={message.content}
                        contentAttributes={message.contentAttributes}
                      />
                    ) : parseSupportMessageForm(message.contentAttributes).isForm ? (
                      <WorkspaceFormCard
                        content={message.content}
                        contentAttributes={message.contentAttributes}
                      />
                    ) : (
                      <div className={`rounded-2xl px-3.5 py-2.5 text-[15px] leading-relaxed break-words ${
                        conversation?.channelType === ChannelType.Email
                          ? `border ${theme.border} ${theme.surface} ${theme.text}`
                          : tone.bubble
                      }`}>
                        {conversation?.channelType === ChannelType.Email ? (
                        <EmailMessageBubble
                          content={message.content}
                          contentAttributes={message.contentAttributes}
                          conversationId={message.conversationId}
                          messageId={message.id}
                          formLinkTitles={formLinkTitles}
                        />
                        ) : (
                        <>
                        <SupportMessageMediaGallery
                          contentAttributes={message.contentAttributes}
                          conversationId={message.conversationId}
                          messageId={message.id}
                        />
                        {message.senderType === SupportSenderType.Bot ? (
                          <SupportBotMessageContent
                            content={message.content}
                            contentAttributes={message.contentAttributes}
                          />
                        ) : (
                          <span className="whitespace-pre-wrap">
                            {message.content ? (
                              <FormPublicLinkText content={message.content} titles={formLinkTitles} />
                            ) : parseSupportMessageMedia(message.contentAttributes).length === 0 ? (
                              <em className="opacity-70">(sem conteúdo)</em>
                            ) : null}
                          </span>
                        )}
                        </>
                        )}
                      </div>
                    )}
                    {(() => {
                      const delivery = parseSupportMessageDelivery(message.contentAttributes);
                      const canRetry = Boolean(onRetryMessage)
                        && canRetrySupportMessage(message, nowMs)
                        && !isClosedForMessaging;
                      const showFailure = isPublicOutgoingMessage(message) && isFailedSupportMessage(message);
                      const showPending = isPublicOutgoingMessage(message) && isPendingSupportMessage(message);
                      if (!showFailure && !showPending && !canRetry)
                      {
                        return null;
                      }

                      return (
                        <div className={`rounded-lg border px-2.5 py-1.5 text-[12px] ${
                          showFailure
                            ? 'border-red-200 bg-red-50 text-red-800'
                            : 'border-amber-200 bg-amber-50 text-amber-800'
                        }`}>
                          {showFailure && (
                            <p>
                              {delivery.error || 'Não foi possível enviar a mensagem.'}
                            </p>
                          )}
                          {showPending && !canRetry && (
                            <p>Enviando…</p>
                          )}
                          {showPending && canRetry && (
                            <p>O envio ficou pendente. Você pode tentar novamente.</p>
                          )}
                          {delivery.outcomeUnknown && (
                            <p className="mt-1">
                              Não foi possível confirmar se o cliente recebeu. Reenviar pode duplicar a mensagem.
                            </p>
                          )}
                          {canRetry && onRetryMessage && (
                            <button
                              type="button"
                              onClick={() => onRetryMessage(message.id)}
                              disabled={retryingMessageId === message.id}
                              className="mt-1.5 inline-flex items-center rounded-md bg-white px-2 py-0.5 text-[11px] font-semibold text-red-700 ring-1 ring-red-200 hover:bg-red-100 disabled:opacity-50"
                            >
                              {retryingMessageId === message.id ? 'Reenviando…' : 'Reenviar'}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                    <div className={`flex items-center gap-1 px-1 ${theme.faint} ${tone.wrap === 'self-end' ? 'justify-end' : ''}`}>
                      <span className="text-[10.5px]">
                        {showAgentAvatar
                          ? time
                          : `${senderLabel(message.senderType, message.messageType, conversation?.contactName ?? 'Contato', message.senderName)} · ${time}`}
                      </span>
                      {canSpeakWorkspaceMessage(conversation?.channelType, message) ? (
                        <MessageAudioControls
                          text={message.content ?? ''}
                          messageType="assistant"
                          buttonClassName={`${theme.muted} hover:text-blue-600`}
                        />
                      ) : null}
                    </div>
                  </div>
                  {showAgentAvatar && (
                    <span className="relative group mb-5 flex-shrink-0">
                      <span
                        className="h-5 w-5 rounded-full grid place-items-center text-[10px] font-bold text-white"
                        style={{ backgroundColor: senderAvatarColor(agentName) }}
                        aria-label={`Enviado por ${agentName}`}
                      >
                        {senderInitial(agentName)}
                      </span>
                      <span className="pointer-events-none absolute right-full mr-1.5 top-1/2 -translate-y-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-[10px] font-medium text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
                        Enviado por: {agentName}
                      </span>
                    </span>
                  )}
                  </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={endRef} />
          </>
        )}
      </div>

      <div
        className={`flex-shrink-0 border-t p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] ${theme.surface} ${theme.border}`}
        onPointerDown={handleUserReadIntent}
      >
        {isClosedForMessaging ? (
          <div className={`flex flex-col gap-2 rounded-xl border px-3 py-3 text-sm ${theme.input}`}>
            <p className={theme.muted}>
              Este atendimento está finalizado. Reabra para enviar mensagens.
            </p>
            {canReopen && (
              <button
                type="button"
                onClick={onReopen}
                disabled={actionLoading}
                className={`self-start inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg ${theme.accentBtn} disabled:opacity-50`}
              >
                Reabrir
              </button>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {assumeHintReason && (
              <WorkspaceAssumeHintBanner
                theme={theme}
                reason={assumeHintReason}
                assigneeName={conversation?.assigneeName}
                loading={actionLoading}
                onAssume={() => {
                  void handleAssumeFromHint();
                }}
                onDismiss={handleDismissAssumeHint}
              />
            )}
            {isEmailConversation && (
              <div className={`mb-2 flex items-center gap-3 text-xs ${theme.faint}`}>
                <button
                  type="button"
                  onClick={toggleSignature}
                  disabled={!(messageSignature.trim() || hasSignatureImage)}
                  className={`inline-flex items-center gap-2 rounded-md px-2 py-1 ${
                    includeSignature ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100'
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                  aria-pressed={includeSignature}
                >
                  <span
                    className={`h-3 w-3 rounded-full border ${
                      includeSignature ? 'border-blue-600 bg-blue-600' : 'border-gray-400'
                    }`}
                  />
                  Incluir assinatura
                </button>
                {!(messageSignature.trim() || hasSignatureImage) && (
                  <Link
                    to={helpdeskRoutes.settingsUserSignature}
                    className="text-blue-600 hover:underline"
                  >
                    Configurar assinatura
                  </Link>
                )}
                {hasSignatureImage && includeSignature && (
                  <span className="ml-auto">Imagem será incorporada ao e-mail</span>
                )}
              </div>
            )}
            <div className={`relative rounded-xl border px-3 py-2 ${theme.input}`}>
              <textarea
                ref={messageInputRef}
                rows={enterSendsMessage ? 2 : 4}
                value={draft}
                onChange={(event) => handleDraftChange(event.target.value)}
                onKeyDown={handleMessageKeyDown}
                placeholder="Escreva uma resposta… (use / para respostas prontas e formulários)"
                disabled={!conversation}
                className="w-full resize-none bg-transparent border-0 p-0 text-base lg:text-sm focus:ring-0 focus:outline-none disabled:opacity-50"
                aria-expanded={showSlashMenu && slashEntries.length > 0}
                aria-controls="workspace-slash-menu"
                aria-autocomplete="list"
              />
              {showSlashMenu && slashEntries.length > 0 && (
                <div
                  id="workspace-slash-menu"
                  role="listbox"
                  aria-label="Atalhos"
                  className={`absolute bottom-full left-0 right-0 mb-1 border rounded-md max-h-64 overflow-y-auto z-10 ${theme.surface} ${theme.border}`}
                >
                  {filteredCannedSuggestions.length > 0 && (
                    <div role="group" aria-label="Respostas prontas">
                      <p className={`px-3 pt-2 pb-1 text-[11px] font-semibold ${theme.muted}`}>
                        Respostas prontas
                      </p>
                      <ul>
                        {filteredCannedSuggestions.map((item) => {
                          const index = slashEntries.findIndex((entry) => entry.key === `canned-${item.id}`);
                          return (
                            <li key={item.id}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={index === highlightedSlashIndex}
                                className={`w-full text-left px-3 py-2 text-sm ${
                                  index === highlightedSlashIndex ? theme.selectedRow : ''
                                }`}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  applyCannedResponse(item);
                                }}
                              >
                                <span className="flex items-start gap-2">
                                  <ChatBubbleLeftIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden="true" />
                                  <span className="min-w-0">
                                    <span className="font-mono text-blue-600">/{item.shortCode}</span>
                                    <span className={`mx-2 ${theme.faint}`}>—</span>
                                    <span className={theme.text}>{item.title}</span>
                                  </span>
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {filteredFormSuggestions.length > 0 && conversation && (
                    <div role="group" aria-label="Formulários">
                      <p className={`px-3 pt-2 pb-1 text-[11px] font-semibold ${theme.muted}`}>
                        Formulários
                      </p>
                      <ul>
                        {filteredFormSuggestions.map((item) => {
                          const index = slashEntries.findIndex((entry) => entry.key === `form-${item.formId}`);
                          return (
                            <li key={item.formId}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={index === highlightedSlashIndex}
                                className={`w-full text-left px-3 py-2 text-sm ${
                                  index === highlightedSlashIndex ? theme.selectedRow : ''
                                }`}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  applyInboxForm(item);
                                }}
                              >
                                <span className="flex items-start gap-2">
                                  <DocumentTextIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" aria-hidden="true" />
                                  <span className="min-w-0">
                                    <span className={`block ${theme.text}`}>{item.formTitle || 'Formulário'}</span>
                                    <span className={`block text-xs ${theme.faint}`}>
                                      {formSlashCaption}
                                    </span>
                                  </span>
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between mt-2">
                <span className={`text-[11px] ${theme.faint}`}>
                  {enterSendsMessage
                    ? '/atalho · Enter envia · Shift+Enter quebra linha · Finalizar vai à próxima'
                    : '/atalho · Enter quebra linha · Ctrl+Enter envia · Finalizar vai à próxima'}
                </span>
                <div className="flex items-center gap-1">
                  <VoiceButton
                    onVoiceResult={handleVoiceResult}
                    onVoiceError={handleVoiceError}
                    disabled={!conversation || sending}
                    idleClassName={`${theme.muted} hover:text-blue-600 hover:bg-blue-600/10`}
                  />
                  <button
                    type="submit"
                    disabled={!conversation || sending || !draft.trim()}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg ${theme.accentBtn} disabled:opacity-50`}
                  >
                    <PaperAirplaneIcon className="h-4 w-4" />
                    {sending ? 'Enviando…' : 'Enviar'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
